import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { ClosingCta } from "@/components/ClosingCta";
import { useLanguage } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { thumbUrl } from "@/lib/images";

export default function About() {
  const [portrait, setPortrait] = useState(null);
  const { t } = useLanguage();
  useSeo({
    title: "Über mich · Wim Jansen Delgado · Fotograf aus Hamburg",
    description:
      "Wim Jansen Delgado fotografiert für Michelin-Restaurants, Hotels und Magazine, unposiert, ehrlich und immer auf der Suche nach dem stillen Moment.",
    path: "/ueber-mich",
  });

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setPortrait(res.data.portrait))
      .catch(() => setPortrait(null));
  }, []);

  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-28 md:px-12 md:pt-36 lg:px-16">
      <header className="mb-16">
        <Reveal>
          <p className="mb-4 font-mono text-sm uppercase tracking-[0.15em] text-smoke">{t("Über mich")}</p>
        </Reveal>
        <h1
          data-testid="about-title"
          className="font-serif font-light leading-[0.95] tracking-tight text-bone"
        >
          <MaskedLine delay={0.1} className="text-[clamp(2.8rem,8vw,6.5rem)]">
            {t("Ich bin")}
          </MaskedLine>
          <MaskedLine delay={0.25} className="text-[clamp(2.8rem,8vw,6.5rem)] italic text-ember">
            Wim.
          </MaskedLine>
        </h1>
      </header>

      <div className="grid grid-cols-12 gap-x-6 gap-y-12 md:gap-x-12">
        {portrait && (
          <div className="col-span-12 md:col-span-5 lg:col-span-5">
            <Reveal delay={0.1}>
              <div
                className="bg-white p-2 md:p-2.5"
                data-testid="about-portrait-frame"
              >
                <img
                  src={thumbUrl(portrait, 1000)}
                  alt={t("Wim Jansen Delgado, Fotograf aus Hamburg")}
                  loading="lazy"
                  decoding="async"
                  className="w-full"
                  data-testid="about-portrait"
                />
              </div>
            </Reveal>
          </div>
        )}
        <div className={`col-span-12 space-y-8 md:col-span-7 ${portrait ? "lg:col-span-6 lg:col-start-7" : "lg:col-span-7"}`}>
          <Reveal>
            <p className="max-w-3xl font-serif text-2xl font-light leading-snug text-bone md:text-4xl">
              {t("Fotograf aus Hamburg.")}
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg">
              {t("Viele Jahre habe ich in der Werbung gearbeitet, zuletzt bei Apple. Mich hat dabei immer interessiert, warum sich etwas richtig anfühlt. Irgendwann wurde die Kamera für mich der ehrlichste Weg, das herauszufinden.")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg">
              {t("Mich zieht an, was oft übersehen wird: das Ungestellte, das Ungeplante, der stille Moment, bevor jemand die Kamera bemerkt.")}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg">
              {t("Meine Arbeit bewegt sich zwischen Restaurants, Hotels und Reisen, nicht weil ich einem Genre folge, sondern weil ich mich dort am lebendigsten fühle: an einem guten Tisch, an einem fremden Ort, mit Menschen, die lieben, was sie tun.")}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg">
              {t("Restaurants sind für mich Orte, an denen die Zeit stehen bleibt. Das Licht, die Räume, das Essen und die Menschen inspirieren mich. Ob mit meiner Familie am Tisch oder unter Köchen am Pass, egal wo auf der Welt: Dort komme ich zur Ruhe, dort fühle ich mich zu Hause. Genau dieses Gefühl halte ich in meinen Bildern fest.")}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg">
              {t("Abseits der Kamera ist das Leben herrlich chaotisch: meine Frau, zwei Kinder, selten ein stiller Moment, außer manchmal am Restauranttisch. Ich möchte es nicht anders haben.")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p
              className="font-mono text-[11px] uppercase leading-loose tracking-[0.25em] text-smoke"
              data-testid="about-references"
            >
              {t("Referenzen: Michelin Guide, Der Feinschmecker, The Fontenay, El Celler de Can Roca u. a.")}
            </p>
          </Reveal>
        </div>
      </div>
      </section>

      <ClosingCta />
    </div>
  );
}
